import { z } from 'zod';
import { fromScaled, toScaled } from './money.ts';
import type { BankMovementKind } from './treasury-cash.ts';

/**
 * Leitura de extratos bancários (ADR 0084, Entrega 2b): OFX (Itaú, XP, BTG, Conta Simples) e o
 * CSV do Mercury. Só lê e organiza: quem grava é a API, depois da prévia, tudo ou nada. Os
 * formatos reais têm particularidades (ver o manual do Financeiro):
 * - OFX em SGML (sem fechar as folhas) ou em XML; conta corrente (BANK*) ou cartão (CC*);
 * - Itaú: linhas "SALDO ANTERIOR" e "SALDO TOTAL DISPONÍVEL DIA" são saldos, não movimentos;
 * - saldo do arquivo (LEDGERBAL) às vezes é da data da exportação, fora do período;
 * - Mercury: uma planilha com várias contas ("Source Account") e operações que falharam.
 */

export const STATEMENT_LAYOUTS = ['ofx', 'csv:mercury'] as const;
export type StatementLayout = (typeof STATEMENT_LAYOUTS)[number];

export interface ParsedStatementLine {
  /** Chave da linha no arquivo: o FITID no OFX; no CSV, o conteúdo que a identifica. */
  key: string;
  date: string;
  /** Com sinal, 4 casas: entrada positiva, saída negativa. */
  amount: string;
  description: string;
  reference: string | null;
  kind: Exclude<BankMovementKind, 'adjustment'>;
}

export interface ParsedStatementBalance {
  date: string;
  amount: string;
  /** daily: saldo do dia (Itaú); previous: saldo anterior ao período; ledger: saldo do arquivo. */
  origin: 'daily' | 'previous' | 'ledger';
}

export type StatementWarning =
  'ledger_balance_outside_period' | 'choose_source_account' | 'failed_lines_skipped' | 'no_lines';

export interface ParsedStatement {
  layout: StatementLayout;
  currency: string | null;
  bankId: string | null;
  accountId: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  lines: ParsedStatementLine[];
  balances: ParsedStatementBalance[];
  warnings: StatementWarning[];
  /** CSV com várias contas: as contas encontradas, para a pessoa escolher. */
  sourceAccounts: string[];
}

export type StatementParseCode = 'not_ofx' | 'not_mercury_csv' | 'invalid_amount' | 'invalid_date';

export class StatementParseError extends Error {
  readonly code: StatementParseCode;
  constructor(code: StatementParseCode) {
    super(code);
    this.code = code;
  }
}

/** Texto do arquivo: UTF-8 quando for válido (o Itaú declara 1252 mas manda UTF-8), senão 1252. */
export function decodeStatementFile(bytes: Uint8Array): string {
  let text: string;
  try {
    text = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    text = new TextDecoder('windows-1252').decode(bytes);
  }
  return text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
}

function amountOf(raw: string): string {
  let v = raw.trim().replace(/\s/g, '');
  if (v.includes(',') && !v.includes('.')) v = v.replace(',', '.');
  if (!/^[-+]?\d+(\.\d+)?$/.test(v)) throw new StatementParseError('invalid_amount');
  v = v.replace(/^\+/, '');
  // Mais de 4 casas não acontece nos extratos; se vier, recusa em vez de arredondar.
  return fromScaled(toScaled(v));
}

/** "20260925162126[-3:GMT]" → "2026-09-25" (a data como o banco informou, sem trocar o fuso). */
function ofxDate(raw: string): string {
  const m = /^(\d{4})(\d{2})(\d{2})/.exec(raw.trim());
  if (!m) throw new StatementParseError('invalid_date');
  return `${m[1]}-${m[2]}-${m[3]}`;
}

const plain = (s: string) =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase().replace(/\s+/g, ' ').trim();

function kindOfOfx(type: string, amount: string): ParsedStatementLine['kind'] {
  switch (type.toUpperCase()) {
    case 'FEE':
    case 'SRVCHG':
      return 'fee';
    case 'INT':
    case 'DIV':
      return 'interest';
    default:
      return amount.startsWith('-') ? 'payment' : 'receipt';
  }
}

const AGGREGATES = new Set([
  'STMTTRN',
  'LEDGERBAL',
  'AVAILBAL',
  'BANKACCTFROM',
  'CCACCTFROM',
  'BANKTRANLIST',
]);

export function parseOfx(text: string): ParsedStatement {
  if (!/<OFX>/i.test(text)) throw new StatementParseError('not_ofx');
  const body = text.slice(text.search(/<OFX>/i));
  const stack: { name: string; fields: Record<string, string> }[] = [];
  const top: Record<string, string> = {};
  const trns: Record<string, string>[] = [];
  const balances: Record<string, Record<string, string>> = {};
  const account: Record<string, string> = {};
  const list: Record<string, string> = {};
  const re = /<(\/?)([A-Za-z0-9.]+)>([^<]*)/g;
  for (let m = re.exec(body); m; m = re.exec(body)) {
    const closing = m[1] === '/';
    const name = m[2]!.toUpperCase();
    const value = m[3]!.replace(/\s+$/, '').replace(/^\s+/, '');
    if (AGGREGATES.has(name)) {
      if (!closing) {
        stack.push({ name, fields: {} });
      } else {
        const done = stack.pop();
        if (!done) continue;
        if (done.name === 'STMTTRN') trns.push(done.fields);
        else if (done.name === 'LEDGERBAL' || done.name === 'AVAILBAL') {
          balances[done.name] = done.fields;
        } else if (done.name === 'BANKACCTFROM' || done.name === 'CCACCTFROM') {
          Object.assign(account, done.fields);
        } else if (done.name === 'BANKTRANLIST') Object.assign(list, done.fields);
      }
      continue;
    }
    if (closing || value === '') continue;
    const target = stack.at(-1)?.fields ?? top;
    target[name] = value;
  }

  const lines: ParsedStatementLine[] = [];
  const out: ParsedStatementBalance[] = [];
  // Itaú: o FITID é a data com a posição da linha na exportação e muda conforme o período pedido.
  // A chave passa a ser o conteúdo (data, valor, histórico) com o número da repetição no arquivo.
  const contentKeyed = /^0*341$/.test(account.BANKID ?? '');
  const seen = new Map<string, number>();
  for (const t of trns) {
    if (!t.TRNAMT || !t.DTPOSTED) continue;
    const amount = amountOf(t.TRNAMT);
    const date = ofxDate(t.DTPOSTED);
    const memo = (t.MEMO ?? t.NAME ?? '').trim();
    const label = plain(memo);
    // Itaú: os saldos vêm como linhas do extrato.
    if (label.startsWith('SALDO TOTAL DISPONIVEL DIA')) {
      out.push({ date, amount, origin: 'daily' });
      continue;
    }
    if (label === 'SALDO ANTERIOR') {
      out.push({ date, amount, origin: 'previous' });
      continue;
    }
    const content = `${date}|${amount}|${label}`;
    const n = (seen.get(content) ?? 0) + 1;
    seen.set(content, n);
    lines.push({
      key: t.FITID && !contentKeyed ? `fit:${t.FITID}` : `ofx:${content}${n === 1 ? '' : `#${n}`}`,
      date,
      amount,
      description: (memo || t.TRNTYPE || '—').slice(0, 300),
      reference: t.CHECKNUM ?? t.REFNUM ?? null,
      kind: kindOfOfx(t.TRNTYPE ?? '', amount),
    });
  }
  const periodStart = list.DTSTART
    ? ofxDate(list.DTSTART)
    : (lines.map((l) => l.date).sort()[0] ?? null);
  const periodEnd = list.DTEND
    ? ofxDate(list.DTEND)
    : (lines
        .map((l) => l.date)
        .sort()
        .at(-1) ?? null);
  const warnings: StatementWarning[] = [];
  const ledger = balances.LEDGERBAL;
  if (ledger?.BALAMT && ledger.DTASOF) {
    const date = ofxDate(ledger.DTASOF);
    if (periodStart && periodEnd && date >= periodStart && date <= periodEnd) {
      // O saldo do dia (Itaú) já diz o mesmo; o do arquivo só entra se não houver.
      if (!out.some((b) => b.date === date)) {
        out.push({ date, amount: amountOf(ledger.BALAMT), origin: 'ledger' });
      }
    } else {
      warnings.push('ledger_balance_outside_period');
    }
  }
  if (lines.length === 0) warnings.push('no_lines');
  return {
    layout: 'ofx',
    currency: top.CURDEF ?? null,
    bankId: account.BANKID ?? null,
    accountId: account.ACCTID ?? null,
    periodStart,
    periodEnd,
    lines,
    balances: out.sort((a, b) => a.date.localeCompare(b.date)),
    warnings,
    sourceAccounts: [],
  };
}

/** CSV com aspas ("" dentro de aspas), vírgula como separador. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i]!;
    if (quoted) {
      if (c === '"' && text[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (c === '"') quoted = false;
      else cell += c;
    } else if (c === '"') quoted = true;
    else if (c === ',') {
      row.push(cell);
      cell = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      row.push(cell);
      if (row.some((v) => v !== '')) rows.push(row);
      row = [];
      cell = '';
    } else cell += c;
  }
  row.push(cell);
  if (row.some((v) => v !== '')) rows.push(row);
  return rows;
}

/** Extrato CSV do Mercury (uma planilha, várias contas): só a conta escolhida, só o enviado. */
export function parseMercuryCsv(text: string, sourceAccount: string | null): ParsedStatement {
  const [header, ...rows] = parseCsv(text);
  const col = (name: string) => header?.findIndex((h) => h.trim() === name) ?? -1;
  const idx = {
    date: col('Date (UTC)'),
    description: col('Description'),
    amount: col('Amount'),
    status: col('Status'),
    source: col('Source Account'),
    bank: col('Bank Description'),
    reference: col('Reference'),
    category: col('Category'),
    timestamp: col('Timestamp'),
    tracking: col('Tracking ID'),
  };
  if (idx.date < 0 || idx.amount < 0 || idx.status < 0 || idx.source < 0) {
    throw new StatementParseError('not_mercury_csv');
  }
  const get = (r: string[], i: number) => (i >= 0 ? (r[i] ?? '').trim() : '');
  const sourceAccounts = [...new Set(rows.map((r) => get(r, idx.source)).filter(Boolean))].sort();
  const warnings: StatementWarning[] = [];
  if (!sourceAccount && sourceAccounts.length > 1) {
    return {
      layout: 'csv:mercury',
      currency: 'USD',
      bankId: null,
      accountId: null,
      periodStart: null,
      periodEnd: null,
      lines: [],
      balances: [],
      warnings: ['choose_source_account'],
      sourceAccounts,
    };
  }
  const chosen = sourceAccount ?? sourceAccounts[0] ?? null;
  const lines: ParsedStatementLine[] = [];
  let failed = 0;
  for (const r of rows) {
    if (get(r, idx.source) !== chosen) continue;
    if (get(r, idx.status) !== 'Sent') {
      failed++;
      continue;
    }
    const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(get(r, idx.date));
    if (!m) throw new StatementParseError('invalid_date');
    const date = `${m[3]}-${m[1]}-${m[2]}`;
    const amount = amountOf(get(r, idx.amount));
    const description = get(r, idx.description);
    const bank = get(r, idx.bank);
    const category = get(r, idx.category);
    const tracking = get(r, idx.tracking);
    lines.push({
      key: `mercury:${[get(r, idx.timestamp), amount, description, chosen, tracking].join('|')}`,
      date,
      amount,
      description:
        [description, bank && bank !== description ? bank : '']
          .filter(Boolean)
          .join(' · ')
          .slice(0, 300) || '—',
      reference: get(r, idx.reference) || tracking || null,
      kind:
        category === 'Bank Fees'
          ? 'fee'
          : category === 'Taxes'
            ? 'tax'
            : amount.startsWith('-')
              ? 'payment'
              : 'receipt',
    });
  }
  if (failed) warnings.push('failed_lines_skipped');
  if (lines.length === 0) warnings.push('no_lines');
  const dates = lines.map((l) => l.date).sort();
  return {
    layout: 'csv:mercury',
    currency: 'USD',
    bankId: null,
    accountId: chosen,
    periodStart: dates[0] ?? null,
    periodEnd: dates.at(-1) ?? null,
    lines,
    balances: [],
    warnings,
    sourceAccounts,
  };
}

export function parseStatement(
  layout: StatementLayout,
  text: string,
  sourceAccount: string | null = null,
): ParsedStatement {
  return layout === 'ofx' ? parseOfx(text) : parseMercuryCsv(text, sourceAccount);
}

export const statementImportInput = z.object({
  accountKey: z.uuid(),
  layout: z.enum(STATEMENT_LAYOUTS),
  fileName: z.string().trim().min(1).max(255),
  /** O arquivo em base64 (até ~2 MB). */
  contentBase64: z.string().min(1).max(3_000_000),
  sourceAccount: z.string().trim().max(200).nullable().optional(),
  /**
   * Linhas que a prévia apontou como possível repetição de uma linha já lançada (digitada ou de
   * outro arquivo) e que a pessoa confirmou que são outras: entram mesmo assim.
   */
  include: z.array(z.string().min(1).max(300)).max(5000).optional(),
});
export type StatementImportInput = z.infer<typeof statementImportInput>;
